import React, {useState, useEffect} from 'react';
import {useFormikContext} from 'formik';
import {useTranslation} from 'react-i18next';
import {CreateDesignType} from '../../../../types/TypesStore';
import {ApproveBonusSmsCode, UserClient} from '../../../../types/TypesUser';
import {CustomInput} from '../../../../Components/Input';
import GreenButton from '../../../../Components/Buttons/GreenButton';
import check from '../../../../assets/img/Cart/ant-design_check-square-outlined.svg';
import {
	useApproveBonusSmsMutation,
	useSendBonusSmsMutation,
} from '../../../../redux/userClientsApi';
import {handleApiCall} from '../../../../assets/utils/utils';
import {toast} from 'react-toastify';
import s from './BonusHandler.module.css';

type BonusHandlerProps = {
	client: UserClient | null;
	onBonusConfirmed?: (confirmed: boolean) => void;
};

const BonusHandler: React.FC<BonusHandlerProps> = ({
	client,
	onBonusConfirmed,
}) => {
	const {t} = useTranslation();
	const {values, setFieldValue} = useFormikContext<CreateDesignType>();
	const [openCodeInput, setOpenCodeInput] = useState(false);
	const [isBonusConfirmed, setIsBonusConfirmed] = useState(false);
	const [sendBonusSms] = useSendBonusSmsMutation();
	const [approveBonusSms] = useApproveBonusSmsMutation();

	// Сброс состояния при смене клиента
	useEffect(() => {
		if (!client) {
			setFieldValue('bonus', '');
			setFieldValue('approveCode', undefined);
			setOpenCodeInput(false);
			setIsBonusConfirmed(false);
			if (onBonusConfirmed) {
				onBonusConfirmed(false);
			}
		}
	}, [client, setFieldValue, onBonusConfirmed]);

	// Уведомление родительского компонента о подтверждении бонусов
	useEffect(() => {
		if (onBonusConfirmed) {
			onBonusConfirmed(isBonusConfirmed);
		}
	}, [isBonusConfirmed, onBonusConfirmed]);

	const handleSendCode = async () => {
		if (client?.client_id) {
			await handleApiCall({
				apiCall: () => sendBonusSms(client.client_id),
				onSuccess: (data) => {
					if (data.approve) {
						toast.success(data.message.textmsg);
						setOpenCodeInput(true);
					}
				},
				onError: (error) => {
					toast.error(error);
				},
			});
		}
	};

	const handleApproveCode = async (code: number) => {
		if (client?.client_id) {
			const data: ApproveBonusSmsCode = {
				code: code,
				client_id: client.client_id,
				action: 'bonus',
			};
			await handleApiCall({
				apiCall: () => approveBonusSms(data),
				onSuccess: (data) => {
					if (data.approve) {
						toast.success(data.message.textmsg);
						setIsBonusConfirmed(true);
					}
				},
				onError: (error) => {
					toast.error(error);
				},
			});
		}
	};

	// Проверяем, можно ли сохранить без подтверждения бонусов
	const canSaveWithoutBonusConfirmation = () => {
		const bonusAmount = +values.bonus;
		const hasClientMoney = client?.money && +client.money > 0;

		// Если нет бонусов у клиента или клиент не указал сумму бонусов - можно сохранить
		if (!hasClientMoney || bonusAmount === 0 || values.bonus === '') {
			return true;
		}

		// Если указаны бонусы - нужно подтверждение
		return isBonusConfirmed && values.approveCode;
	};

	// Если у клиента нет бонусов, не показываем компонент
	if (!client?.money || +client.money === 0) {
		return null;
	}

	return (
		<div className={s.bonusHandlerContainer}>
			<div className={s.title}>
				{t('Ви можете цими грошима оплатити частину оформлення')}
			</div>
			<div className={s.availableBonusInfo}>
				{t('Доступно бонусів')}:{' '}
				<strong>
					{client.money} {t('грн')}
				</strong>
			</div>
			<div className={`${s.input} ${s.flex}`}>
				<CustomInput
					name={'bonus'}
					placeholder={t('Введіть бажану суму бонусів')}
					disabled={isBonusConfirmed}
				/>
				{isBonusConfirmed && <img src={check} alt="icon" />}
			</div>
			{values.bonus !== '' && values.bonus <= client.money && (
				<div className={s.buttonContainer}>
					<GreenButton
						text={t('Відправити код')}
						type={'button'}
						onClick={handleSendCode}
					/>
				</div>
			)}
			{openCodeInput && (
				<div className={`${s.input} ${s.buttonContainer}`}>
					<CustomInput
						name={'approveCode'}
						placeholder={`${t('Введіть код з')} SMS`}
						autoComplete="one-time-code"
					/>
					{values && values.approveCode && (
						<div className={s.buttonContainer}>
							<GreenButton
								text={t('Підтвердити код')}
								type={'button'}
								disabled={values.approveCode.toString().length < 6}
								onClick={() =>
									values.approveCode && handleApproveCode(values.approveCode)
								}
							/>
						</div>
					)}
				</div>
			)}
			{/* Информационное сообщение о статусе бонусов */}
			<div className={s.infoMessage}>
				{isBonusConfirmed && +values.bonus > 0 && values.bonus !== '' ? (
					<div className={s.successText}>
						{t('Бонуси успішно підтверджені через SMS')}
					</div>
				) : +values.bonus > 0 && values.bonus !== '' ? (
					<div className={s.warningText}>
						{t('Для використання бонусів потрібно підтвердження через SMS')}
					</div>
				) : (
					<div className={s.infoText}>
						{t('Ви можете зберегти оформлення без використання бонусів')}
					</div>
				)}
			</div>
		</div>
	);
};

export default BonusHandler;
export type {BonusHandlerProps};
